import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Pages/PagesCss/NavBar.css';
import logo from './assets/logo.png'; // Make sure you have a logo in this path

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const toggleMobile = () => setMobileOpen(!mobileOpen);
  
  const closeMobileMenu = () => { 
    setMobileOpen(false); 
    setDropdownOpen(null); 
  };

  // Close dropdowns when route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  const menuItems = [
    { name: "Home", path: "/" },
    {
      name: "Credit Management",
      dropdown: [
        { name: "Credit Card Payoff Calculator", path: "/CreditCardPayoffCalculator" },
        { name: "Minimum Payment Interest Calculator", path: "/PaymentInterestCalculator" },
        { name: "Credit Card Interest Calculator", path: "/CreditCardInterestCalculator" },
      ],
    },
    {
      name: "Savings",
      dropdown: [
        { name: "Balance Transfer Savings Calculator", path: "/BalanceTransferCalculator" },
        { name: "Cashback & Rewards Comparison Tool", path: "/CashbackRewardsCalculator" },
        { name: "Credit Card Limit Increase Estimator", path: "/CreditLimitIncreaseEstimator" },
      ],
    },
    {
      name: "Credit",
      dropdown: [
        { name: "Credit Utilization Ratio Calculator", path: "/CreditUtilizationCalculator" },
        { name: "Debt-to-Income (DTI) Ratio Calculator", path: "/DTICalculator" },
      ],
    },
    {
      name: "Planning",
      dropdown: [
        { name: "Card Approval Odds Checker", path: "/CardApprovalOddsChecker" },
        { name: "Credit Card Upgrade Analyzer", path: "/CreditCardUpgradeAnalyzer" },
        { name: "Annual Fee Value Calculator", path: "/AnnualFeeValueCalculator" },
      ],
    },
    { name: "Blog", path: "https://www.blog.creditsiq.com" },
  ];

  const splitDropdownItems = (items) => {
    const itemsPerColumn = Math.ceil(items.length / 2);
    return [
      items.slice(0, itemsPerColumn),
      items.slice(itemsPerColumn)
    ];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Keyboard navigation
  const handleKeyDown = (event, index) => {
    if (event.key === 'Escape') {
      setDropdownOpen(null);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown(index);
    }
  };

  return (
    <header className="navigation" ref={navRef} role="navigation">
      <div className="nav-container">
        <NavLink 
          to="/" 
          className="brand" 
          onClick={closeMobileMenu}
          aria-label="CreditsIQ Home"
        >
          <img src={logo} alt="CreditsIQ Logo" className="logo" />
          <span className="brand-text">CreditsIQ</span>
        </NavLink>

        <button 
          className={`nav-mobile ${mobileOpen ? "active" : ""}`}
          onClick={toggleMobile}
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation menu"
          aria-controls="main-navigation"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav id="main-navigation">
          <ul className={`nav-menu ${mobileOpen ? "open" : ""}`}>
            {menuItems.map((item, idx) => (
              <li key={idx} className="nav-item">
                {item.dropdown ? (
                  <>
                    <button 
                      className={`nav-link dropdown-btn ${dropdownOpen === idx ? "active" : ""}`}
                      onClick={() => toggleDropdown(idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      aria-expanded={dropdownOpen === idx}
                      aria-haspopup="true"
                      aria-controls={`dropdown-${idx}`}
                    >
                      {item.name} 
                      <span className={`arrow ${dropdownOpen === idx ? "rotate" : ""}`} aria-hidden="true">
                        ▼
                      </span>
                    </button>
                    <div 
                      id={`dropdown-${idx}`}
                      className={`nav-dropdown ${dropdownOpen === idx ? "show" : ""}`}
                      role="menu"
                    >
                      <div className="dropdown-columns">
                        {splitDropdownItems(item.dropdown).map((column, colIdx) => (
                          <div className="dropdown-column" key={colIdx}>
                            {column.map((sub, subIdx) => (
                              <NavLink 
                                key={subIdx}
                                to={sub.path}
                                onClick={closeMobileMenu}
                                className={({ isActive }) => `dropdown-item ${isActive ? "active" : ""}`}
                                role="menuitem"
                              >
                                {sub.name}
                              </NavLink>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  item.path.startsWith("http") ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link"
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <NavLink 
                      to={item.path} 
                      onClick={closeMobileMenu}
                      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    >
                      {item.name}
                    </NavLink>
                  )
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default NavBar;